import { useState, useEffect, useContext } from 'react'
import { useT } from '../hooks'
import AuthContext from 'contexte/AuthContext'
import Badge from 'components/Badge/Badge'
import { launchError, launchSuccess } from 'components/notifications'
import { fetchBBookExposure, updateBBookExposure, updateBBookStatus } from 'services/bBookServices'
import BBookAmountField from './BBookAmountField'
import ConfirmModal from 'components/ConfirmModal/ConfirmModal'
import BBookLimits from './BBookLimits'
import { euro } from '../report/charts'

const isPositive = (value) => value !== '' && Number.isFinite(Number(value)) && Number(value) > 0

// The kill switch, the engine-owned brakes and the exposure caps: everything that decides whether the house takes a trade.
const BBookStatusCard = ({ status, setStatus }) => {
  const { t } = useT()
  const { user } = useContext(AuthContext)

  const [confirmToggle, setConfirmToggle] = useState(false)
  const [updatingEnabled, setUpdatingEnabled] = useState(false)
  const [saved, setSaved] = useState({ low: null, high: null })
  const [values, setValues] = useState({ low: '', high: '' })
  const [touched, setTouched] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchBBookExposure(user)
        setSaved(data)
        setValues({ low: data.low ?? '', high: data.high ?? '' })
      } catch (e) {
        launchError(t('notifications.b_book.settings.error'))
      }
    }
    load()
  }, [])

  const toggleEnabled = async () => {
    const next = !status.enabled
    try {
      setUpdatingEnabled(true)
      const data = await updateBBookStatus(user, next)
      setStatus(prev => ({ ...prev, enabled: data.enabled }))
      launchSuccess(t(`pages.b_book.status.${data.enabled ? 'enabled_success' : 'disabled_success'}`))
    } catch (e) {
      launchError(t('notifications.b_book.settings.error'))
    } finally {
      setUpdatingEnabled(false)
      setConfirmToggle(false)
    }
  }

  const errors = {
    low: !isPositive(values.low) ? t('pages.b_book.exposure.error_positive')
      : isPositive(values.high) && Number(values.low) >= Number(values.high) ? t('pages.b_book.exposure.error_order', { high: euro(values.high) }) : null,
    high: !isPositive(values.high) ? t('pages.b_book.exposure.error_positive') : null,
  }
  const dirty = String(values.low) !== String(saved.low ?? '') || String(values.high) !== String(saved.high ?? '')
  const shownError = (field) => (touched[field] ? errors[field] : null) // validated on blur, not on every keystroke

  const saveExposure = async () => {
    if (errors.low || errors.high) {
      setTouched({ low: true, high: true })
      return document.getElementById(errors.low ? 'exposure-low' : 'exposure-high')?.focus() // first invalid field
    }
    try {
      setSaving(true)
      const data = await updateBBookExposure(user, Number(values.low), Number(values.high))
      setSaved({ low: data.low, high: data.high })
      setTouched({})
      if (data.applied_live) launchSuccess(t('notifications.b_book.settings.success'))
      else launchError(t('notifications.b_book.engine_not_listening'))
    } catch (e) {
      launchError(t('notifications.b_book.settings.error'))
    } finally {
      setSaving(false)
    }
  }

  // engine-owned: shown, never set from here; an active brake explains itself in plain text, not only in a tooltip
  const brakes = [
    { key: 'engine', on: status.engine_alive && status.engine_listening, good: true },
    { key: 'force_a_book', on: status.force_a_book, good: false },
    { key: 'over_max_loss', on: status.over_max_loss, good: false },
  ]
  const tripped = brakes.filter(({ on, good }) => on !== good)

  return (
    <section className='b_book_card' aria-labelledby='b-book-status-title'>
      <h2 id='b-book-status-title' className='b_book_visually_hidden'>{t('pages.b_book.status.section')}</h2>

      <div className='b_book_status_row'>
        <div className='b_book_status_label'>
          <p id='b-book-enabled-label'>{t('pages.b_book.status.label')}</p>
          <Badge text={t(`pages.b_book.status.${status.enabled ? 'enabled' : 'disabled'}`)} color={status.enabled ? 'var(--bb-ok-badge)' : 'var(--bb-alert-badge)'} />
        </div>
        <label className={`toggle b_book_toggle ${updatingEnabled ? 'b_book_toggle_disabled' : ''}`}>
          <input className="toggle-checkbox" type="checkbox" role="switch" aria-labelledby='b-book-enabled-label' aria-describedby='b-book-enabled-hint'
            checked={!!status.enabled} disabled={updatingEnabled} onChange={() => setConfirmToggle(true)} />
          <div className="toggle-switch"></div>
        </label>
      </div>
      <p id='b-book-enabled-hint' className='b_book_row_hint'>{t('pages.b_book.status.hint')}</p>

      <div className='b_book_status_row b_book_status_row_top'>
        <div className='b_book_status_label'><p>{t('pages.b_book.brakes.label')}</p></div>
        <div className='b_book_brakes_block'>
          <div className='b_book_brakes'>
            {brakes.map(({ key, on, good }) => (
              <span key={key} title={t(`pages.b_book.brakes.${key}_hint`)}>
                <Badge text={t(`pages.b_book.brakes.${key}_${on ? 'on' : 'off'}`)} color={on === good ? 'var(--bb-ok-badge)' : 'var(--bb-alert-badge)'} />
              </span>
            ))}
          </div>
          {tripped.length > 0 && (
            <ul className='b_book_brake_notes'>
              {tripped.map(({ key }) => <li key={key}>{t(`pages.b_book.brakes.${key}_hint`)}</li>)}
            </ul>
          )}
        </div>
      </div>

      <fieldset className='b_book_status_row b_book_form_row'>
        <legend className='b_book_status_label'><p>{t('pages.b_book.exposure.label')}</p></legend>
        <div className='b_book_form_fields'>
          <BBookAmountField id='exposure-low' label={t('pages.b_book.exposure.low')} helper={t('pages.b_book.exposure.low_hint')}
            value={values.low} error={shownError('low')} disabled={saving}
            onChange={(value) => setValues(prev => ({ ...prev, low: value }))} onBlur={() => setTouched(prev => ({ ...prev, low: true }))} />
          <BBookAmountField id='exposure-high' label={t('pages.b_book.exposure.high')} helper={t('pages.b_book.exposure.high_hint')}
            value={values.high} error={shownError('high')} disabled={saving}
            onChange={(value) => setValues(prev => ({ ...prev, high: value }))} onBlur={() => setTouched(prev => ({ ...prev, high: true }))} />
          <button className='b_book_action_button b_book_form_submit' disabled={saving || !dirty} aria-busy={saving || undefined} onClick={saveExposure}>
            {saving ? t('pages.b_book.saving') : t('pages.b_book.exposure.save')}
          </button>
        </div>
      </fieldset>

      <BBookLimits />

      <ConfirmModal
        isOpen={confirmToggle}
        loading={updatingEnabled}
        danger={status.enabled}
        title={t(`pages.b_book.status.${status.enabled ? 'confirm_off_title' : 'confirm_on_title'}`)}
        message={t(`pages.b_book.status.${status.enabled ? 'confirm_off_message' : 'confirm_on_message'}`)}
        confirmLabel={t(`pages.b_book.status.${status.enabled ? 'confirm_off' : 'confirm_on'}`)}
        cancelLabel={t('pages.b_book.positions.cancel')}
        onConfirm={toggleEnabled}
        onClose={() => setConfirmToggle(false)}
      />
    </section>
  )
}

export default BBookStatusCard
